import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BookOpen,
  Award,
  TrendingUp,
  Clock,
  Target,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { enrollmentService, EnrollmentWithProduct } from '@/services/enrollment.service';
import { trackDashboard, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

const DashboardOverview: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user.user);
  const [enrollments, setEnrollments] = useState<EnrollmentWithProduct[]>([]);
  const [loading, setLoading] = useState(true);

  // Track scroll depth and page view
  useScrollTracking('DashboardOverview');

  useEffect(() => {
    trackDashboard.view('overview');
    trackPageView('DashboardOverview');
  }, []);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        setLoading(true);
        const response = await enrollmentService.getMyEnrollments();
        
        if (response.success && response.data) {
          setEnrollments(response.data);
        }
      } catch (err) {
        console.error('Error fetching enrollments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  // Calculate statistics
  const totalCourses = enrollments.length;
  const completedCourses = enrollments.filter(e => e.progress_percentage === 100).length;
  const certificatesEarned = enrollments.filter(e => e.certificate_issued).length;
  const inProgressCourses = enrollments.filter(e => e.progress_percentage > 0 && e.progress_percentage < 100).length;
  const averageProgress = totalCourses > 0 
    ? enrollments.reduce((sum, e) => sum + e.progress_percentage, 0) / totalCourses 
    : 0;

  // Get recent activity (last 3 courses with progress)
  const recentActivity = enrollments
    .filter(e => e.progress_percentage > 0)
    .sort((a, b) => new Date(b.enrolled_at).getTime() - new Date(a.enrolled_at).getTime())
    .slice(0, 3);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">
          Welcome back, {user?.first_name || 'Student'}!
        </h1>
        <p className="text-gray-300 text-lg">
          Here's your learning overview
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Courses */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-xl p-6 hover:border-indigo-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Total Courses</h3>
            <BookOpen className="w-5 h-5 text-indigo-400" />
          </div>
          <p className="text-3xl font-bold text-white">{totalCourses}</p>
          <p className="text-xs text-gray-500 mt-1">
            {inProgressCourses} in progress
          </p>
        </div>

        {/* Completed */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-green-500/30 rounded-xl p-6 hover:border-green-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Completed</h3>
            <Target className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-3xl font-bold text-white">{completedCourses}</p>
          <p className="text-xs text-gray-500 mt-1">
            {totalCourses > 0 ? ((completedCourses / totalCourses) * 100).toFixed(0) : 0}% completion rate
          </p>
        </div>

        {/* Certificates */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-purple-500/30 rounded-xl p-6 hover:border-purple-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Certificates</h3>
            <Award className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-3xl font-bold text-white">{certificatesEarned}</p>
          <p className="text-xs text-gray-500 mt-1">
            Earned certificates
          </p>
        </div>

        {/* Average Progress */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-blue-500/30 rounded-xl p-6 hover:border-blue-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Avg. Progress</h3>
            <TrendingUp className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-3xl font-bold text-white">{averageProgress.toFixed(0)}%</p>
          <p className="text-xs text-gray-500 mt-1">
            Across all courses
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Recent Activity</h2>
            <button 
              onClick={() => navigate('/dashboard/courses')}
              className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-medium flex items-center gap-1"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {recentActivity.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Recent Activity</h3>
              <p className="text-gray-400 mb-6">Start learning to see your progress here</p>
              <button
                onClick={() => navigate('/courses')}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
              >
                Browse Courses
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((enrollment) => (
                <div
                  key={enrollment.id}
                  onClick={() => navigate('/dashboard/courses')}
                  className="flex items-center gap-4 p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900/70 transition-all cursor-pointer border border-transparent hover:border-indigo-500/30"
                >
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex-shrink-0 flex items-center justify-center">
                    {enrollment.product_details?.thumbnail_url ? (
                      <img 
                        src={enrollment.product_details.thumbnail_url} 
                        alt={enrollment.product_title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <BookOpen className="w-8 h-8 text-white/70" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">
                      {enrollment.product_title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${enrollment.progress_percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-400 font-medium">
                        {enrollment.progress_percentage.toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {enrollment.certificate_issued && (
                    <Award className="w-6 h-6 text-green-400 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Quick Actions</h2>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                trackDashboard.quickActionClick('browse_courses');
                navigate('/courses');
              }}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all text-left"
            >
              <BookOpen className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Browse New Courses</span>
            </button>

            <button
              onClick={() => {
                trackDashboard.quickActionClick('continue_learning');
                navigate('/dashboard/courses');
              }}
              className="w-full flex items-center gap-3 p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900/70 transition-all text-left border border-white/10 hover:border-indigo-500/30"
            >
              <Target className="w-5 h-5 text-gray-400" />
              <span className="text-gray-300 font-medium">Continue Learning</span>
            </button>

            <button
              onClick={() => {
                trackDashboard.quickActionClick('view_progress');
                navigate('/dashboard/learning');
              }}
              className="w-full flex items-center gap-3 p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900/70 transition-all text-left border border-white/10 hover:border-indigo-500/30"
            >
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="text-gray-300 font-medium">View Progress</span>
            </button>

            <button
              onClick={() => {
                trackDashboard.quickActionClick('account_settings');
                navigate('/dashboard/settings');
              }}
              className="w-full flex items-center gap-3 p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900/70 transition-all text-left border border-white/10 hover:border-indigo-500/30"
            >
              <Target className="w-5 h-5 text-gray-400" />
              <span className="text-gray-300 font-medium">Account Settings</span>
            </button>
          </div>

          {/* Learning Streak */}
          <div className="mt-6 p-4 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg border border-indigo-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Learning Streak</span>
              <TrendingUp className="w-4 h-4 text-indigo-400" />
            </div>
            <p className="text-2xl font-bold text-white">0 days</p>
            <p className="text-xs text-gray-500 mt-1">Keep learning to build your streak!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
